import {useCallback, useEffect, useMemo} from "react";
import {
    CustomsRequirement, customsRequirementListPathBuilder,
    GetCustomsRequirementListDocument,
    GetCustomsRequirementListQueryResult,
    GetCustomsRequirementListQueryVars
} from "~/apps/packages/customs-requirements/queries";
import {useQuery} from "~/apollo/patches";
import {useErrorHandler} from "~/components/ErrorHandling";
import {useApolloClient} from "@apollo/client";


type useCustomsRequirementListOptions = {
    query?: string
    dateRange?: [Date, Date]
    screeningType?: CustomsRequirement["screeningType"]
}

export const useCustomsRequirementsList = (options?: useCustomsRequirementListOptions) => {
    const handleError = useErrorHandler();
    const apollo = useApolloClient();
    const {data, loading, error, refetch, fetchMore: _fetchMore} = useQuery<GetCustomsRequirementListQueryResult, GetCustomsRequirementListQueryVars>(GetCustomsRequirementListDocument, {
        notifyOnNetworkStatusChange: true,
        variables: {
            dateFrom: options.dateRange ? options.dateRange[0]?.getTime() : null,
            dateTo: options.dateRange ? options.dateRange[1]?.getTime() : null,
            screeningType: options.screeningType,
            query: options.query,
            pathBuilder: customsRequirementListPathBuilder
        }
    });

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutID = setTimeout(refetch, 5000);
            return () => clearTimeout(timeoutID);
        }
    }, [error]);

    const normalizedData = useMemo(() => {
        return data?.customsRequirements?.customsRequirements
    }, [data]);

    const canFetchMore = Boolean(data?.customsRequirements?.pageInfo?.nextPage) && !loading;
    const fetchMore = useCallback(() => {
        if (!canFetchMore) {
            return;
        }
        _fetchMore({
            variables: {
                page: data.customsRequirements.pageInfo.nextPage
            },
        }).catch(handleError);
    }, [data, canFetchMore]);

    // remove previous cache
    useEffect(() => {
        if (!data && loading) {
            apollo.cache.evict({id: "ROOT_QUERY", fieldName: "customsRequirements"});
        }
    }, [data, loading, apollo])


    return {
        data: normalizedData,
        loading: loading || error != null,
        fetchMore,
        canFetchMore
    }
}